import { useTranslation } from 'next-i18next';
import HotelRoom from '../../../../types/RoomAvailability';
import PeopleIcon from '../../common/Icons/People';
import WifiIcon from '../../common/Icons/Wifi';
import ImageWithFallback from '../../common/ImageWithFallback';
import tailwindConfig from '../../../../tailwind.config';

type Props = {
  children: React.ReactNode;
  hotelRoom: HotelRoom;
  campaignTags: HotelRoom['campaignTags'];
  roomDetails?: boolean;
};

const RoomCard = ({ children, hotelRoom, campaignTags, roomDetails }: Props) => {
  const { t } = useTranslation();
  return (
    <div
      key={hotelRoom.name}
      className="relative flex flex-col rounded-md bg-white shadow-4xl lg:flex-row lg:p-4"
    >
      <div className="lg:mr-5 lg:w-60">
        <div className="relative h-56 w-full lg:h-36">
          <ImageWithFallback
            src={hotelRoom.imageUrl || '/fallback-img-790x394.png'}
            alt="hotel_room_image"
            fallbackSrc="/fallback-img-790x394.png"
            layout="fill"
            objectFit="cover"
            sizes="(max-width: 768px) 50vw, 320px"
            className="size-full rounded-md"
          />
          <div className="absolute bottom-0 size-full bg-gradient-to-t from-black lg:hidden" />
        </div>

        {roomDetails && (
          <div className="absolute top-0 flex h-56 flex-col justify-end p-4 lg:static lg:mt-4 lg:block lg:h-auto lg:p-0">
            <p className="pb-2.5 text-medium font-bold text-white lg:text-black">
              {hotelRoom.name}
            </p>
            <div className="flex items-center gap-1 pb-1">
              {/* desktop gray people icon */}
              <div className="hidden lg:block">
                <PeopleIcon size={15} color={tailwindConfig.theme.colors.gray[600]} />
              </div>
              {/* mobile white people icon */}
              <div className="block lg:hidden">
                <PeopleIcon size={15} color={tailwindConfig.theme.colors.white} />
              </div>
              <p className="text-mini text-white lg:text-gray-600">
                {hotelRoom.minOccupancy === hotelRoom.maxOccupancy
                  ? `${hotelRoom.minOccupancy} ${t('hotel:facilities_and_room.people')}`
                  : `${hotelRoom.minOccupancy} ~ ${hotelRoom.maxOccupancy} ${t(
                      'hotel:facilities_and_room.people'
                    )}`}
              </p>
            </div>
            <div className="flex items-center gap-1">
              {/* desktop gray wifi icon */}
              <div className="hidden lg:block">
                <WifiIcon size={15} color={tailwindConfig.theme.colors.gray[600]} />
              </div>
              {/* mobile white wifi icon */}
              <div className="block lg:hidden">
                <WifiIcon size={15} color={tailwindConfig.theme.colors.white} />
              </div>
              <p className="text-mini text-white lg:text-gray-600">
                {hotelRoom.amenities?.highSpeedInternet
                  ? t('hotel:facilities_and_room.wifi')
                  : t('hotel:facilities_and_room.none')}
              </p>
            </div>
            {campaignTags && (
              <div className="pt-2.5 lg:pt-5">
                {campaignTags.map(
                  (x) =>
                    x.url && (
                      <div key={`${x.id}`}>
                        <ImageWithFallback
                          src={x.url}
                          alt={x.alternativeText || ''}
                          fallbackSrc="/fallback-img-3704x2304.png"
                          layout="fixed"
                          objectFit="cover"
                          width={x.width}
                          height={x.height}
                        />
                      </div>
                    )
                )}
              </div>
            )}
          </div>
        )}
      </div>
      <div className="h-fit w-full">{children}</div>
    </div>
  );
};

export default RoomCard;
